import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import CustomLabel from "../components/CustomLabel";
import PageHeader from "../components/PageHeader";
import {
  ADMIN_DATA_SUBTITLES,
  adminNavItemSx,
  adminNavPanelSx,
  adminPageSx,
  adminNavTitleSx,
} from "../components/adminData/adminNavStyles";
import CustomTable from "../components/table/CustomTable";
import PinAccessDialog from "../components/PinAccessDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { COLORS } from "../assets/theme";
import { IPageable, Params } from "../types/table/interfaces.type";
import {
  getCachedPinAccess,
  subscribeToPinAccess,
} from "../utils/pinAccessCache";
import { formatTableDateTime } from "../utils/tableDateFormat";
import {
  AdminNotificationDevice,
  deleteAdminNotificationDevice,
  disableCurrentBrowserWebPush,
  getAdminNotificationDevices,
  getCurrentWebPushEndpoint,
  getStoredCurrentNotificationDeviceId,
} from "../services/backofficeWebPush";

type AdminTab = "clients" | "finance" | "expenses" | "reports" | "notifications";

const showErrorToast = (message: any) =>
  toast.error(String(message), { toastId: String(message) });

const AdminNotificationDevices: React.FC = () => {
  const navigate = useNavigate();
  const activeTab: AdminTab = "notifications";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notificationDevices, setNotificationDevices] = useState<
    AdminNotificationDevice[]
  >([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [deviceRefreshToken, setDeviceRefreshToken] = useState(0);
  const [currentWebPushEndpoint, setCurrentWebPushEndpoint] = useState("");
  const [financeUnlocked, setFinanceUnlocked] = useState(() =>
    getCachedPinAccess(),
  );
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pendingFinancePath, setPendingFinancePath] = useState<string | null>(
    null,
  );
  const [expandedText, setExpandedText] = useState<{
    title: string;
    value: string;
  } | null>(null);
  const [deviceToRemove, setDeviceToRemove] =
    useState<AdminNotificationDevice | null>(null);

  useEffect(() => {
    const loadDevices = async () => {
      setLoadingDevices(true);
      try {
        const [devices, endpoint] = await Promise.all([
          getAdminNotificationDevices(),
          getCurrentWebPushEndpoint().catch(() => ""),
        ]);

        setNotificationDevices(devices);
        setCurrentWebPushEndpoint(endpoint);
      } catch (error) {
        console.error("Failed to fetch notification devices:", error);
        setNotificationDevices([]);
      } finally {
        setLoadingDevices(false);
      }
    };

    loadDevices();
  }, [deviceRefreshToken]);

  useEffect(() => {
    const unsubscribe = subscribeToPinAccess((value) => {
      setFinanceUnlocked(value);
    });
    return unsubscribe;
  }, []);

  const requestFinanceNav = useCallback(
    (path: string) => {
      if (financeUnlocked) {
        navigate(path);
        return;
      }
      setPendingFinancePath(path);
      setPinDialogOpen(true);
    },
    [financeUnlocked, navigate],
  );

  const refreshNotificationDevices = useCallback(() => {
    setDeviceRefreshToken((prev) => prev + 1);
  }, []);

  const handleRemoveNotificationDevice = useCallback(
    async (device: AdminNotificationDevice) => {
      try {
        if (device.endpoint && device.endpoint === currentWebPushEndpoint) {
          const result = await disableCurrentBrowserWebPush(
            device.id !== "current-browser" ? device.id : undefined,
          );

          if (result.status === "disabled") {
            toast.success("Notifications disabled on this device");
          } else if (result.status === "device-not-found") {
            toast.error(
              "Could not match this browser to a backend device row. Use the row remove action for that device.",
            );
          } else {
            toast.info("Browser subscription was removed locally");
          }
        } else {
          await deleteAdminNotificationDevice(device.id);
          toast.success("Notification device removed");
        }

        refreshNotificationDevices();
      } catch (error: any) {
        console.error("Remove notification device failed", error);
        showErrorToast(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to remove notification device",
        );
      }
    },
    [currentWebPushEndpoint, refreshNotificationDevices],
  );

  const handleConfirmRemoveNotificationDevice = useCallback(async () => {
    if (!deviceToRemove) return;

    await handleRemoveNotificationDevice(deviceToRemove);
    setDeviceToRemove(null);
  }, [deviceToRemove, handleRemoveNotificationDevice]);

  const fetchNotificationDevices = useCallback(
    async (
      params: Params<AdminNotificationDevice>,
    ): Promise<IPageable<AdminNotificationDevice>> => ({
      content: notificationDevices,
      number: params.page || 0,
      size: notificationDevices.length || 1,
      totalElements: notificationDevices.length,
      totalPages: 1,
    }),
    [notificationDevices],
  );

  const notificationDeviceActions = useMemo(
    () => [
      {
        id: "remove-device",
        name: "remove-device",
        label: "Remove",
        icon: <DeleteOutlineIcon sx={{ color: COLORS.MainColor }} />,
        handler: (device: AdminNotificationDevice) => setDeviceToRemove(device),
      },
    ],
    [],
  );

  const handleRequestRemoveCurrentDevice = useCallback(() => {
    if (!currentWebPushEndpoint) {
      toast.info("Notifications are not enabled on this browser.");
      return;
    }

    const storedDeviceId = getStoredCurrentNotificationDeviceId();
    const currentDevice = notificationDevices.find(
      (device) => device.endpoint === currentWebPushEndpoint,
    ) || notificationDevices.find(
      (device) => storedDeviceId && device.id === storedDeviceId,
    );

    if (currentDevice) {
      setDeviceToRemove(currentDevice);
      return;
    }

    if (storedDeviceId) {
      setDeviceToRemove({
        id: storedDeviceId,
        endpoint: currentWebPushEndpoint,
        meta: { label: "This browser" },
      });
      return;
    }

    if (notificationDevices.length === 1) {
      setDeviceToRemove(notificationDevices[0]);
      return;
    }

    toast.error(
      "Could not identify this browser's backend device row. Use the row remove action for that device.",
    );
  }, [currentWebPushEndpoint, notificationDevices]);

  const navItems: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: "clients", label: "Clients", icon: <PeopleIcon /> },
    {
      key: "finance",
      label: "Invoices",
      icon: <AccountBalanceWalletOutlinedIcon />,
    },
    { key: "expenses", label: "Expenses", icon: <ReceiptLongOutlinedIcon /> },
    { key: "reports", label: "Reports", icon: <AssessmentOutlinedIcon /> },
    {
      key: "notifications",
      label: "Notification Devices",
      icon: <NotificationsActiveOutlinedIcon />,
    },
  ];

  const activeTabLabel =
    navItems.find((item) => item.key === activeTab)?.label || "Admin Data";

  const renderExpandableText = useCallback(
    (title: string, value?: string | null) => {
      const text = value?.trim() || "-";

      return (
        <Box
          component="button"
          type="button"
          onClick={() => {
            if (text !== "-") {
              setExpandedText({ title, value: text });
            }
          }}
          sx={{
            display: "block",
            width: "100%",
            maxWidth: { xs: 180, sm: 240, md: 320 },
            p: 0,
            m: 0,
            border: 0,
            bgcolor: "transparent",
            color: text === "-" ? "text.secondary" : COLORS.MainColor,
            cursor: text === "-" ? "default" : "pointer",
            font: "inherit",
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: text === "-" ? "none" : "underline",
            textDecorationThickness: "1px",
            textUnderlineOffset: "2px",
          }}
        >
          {text}
        </Box>
      );
    },
    [],
  );

  return (
    <Box
      sx={adminPageSx}
    >
      <Box
        sx={adminNavPanelSx(sidebarCollapsed)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarCollapsed ? "center" : "space-between",
            px: sidebarCollapsed ? 0 : 0.5,
            mb: 1,
          }}
        >
          {!sidebarCollapsed && (
            <CustomLabel text="Admin Data" variant="h6" sx={adminNavTitleSx} />
          )}
          <IconButton
            size="small"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            sx={{ color: COLORS.MainColor }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        <List disablePadding>
          {navItems.map((item) => {
            const selected = activeTab === item.key;
            return (
              <Tooltip
                key={item.key}
                title={sidebarCollapsed ? item.label : ""}
                placement="right"
              >
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    if (item.key === "clients") {
                      navigate("/all-data");
                    } else if (item.key === "finance") {
                      requestFinanceNav("/all-data/invoices");
                    } else if (item.key === "expenses") {
                      requestFinanceNav("/all-data/expenses");
                    } else if (item.key === "reports") {
                      requestFinanceNav("/all-data/reports");
                    }
                  }}
                  sx={adminNavItemSx(selected, sidebarCollapsed)}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarCollapsed ? "auto" : 32,
                      color: "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9rem" }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          flex: 1,
          px: { xs: 1.5, sm: 2.5 },
          // Page is pulled up under the header (adminPageSx); give it back here.
          pt: { xs: 3, sm: 5 },
          pb: { xs: 2, sm: 2.5 },
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <PageHeader
          title={activeTabLabel}
          subtitle={ADMIN_DATA_SUBTITLES[activeTab]}
          icon={navItems.find((item) => item.key === activeTab)?.icon}
        />

        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.04)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <NotificationsActiveOutlinedIcon
                sx={{ color: COLORS.MainColor }}
              />
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  Notification Devices
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage browsers registered for admin push notifications.
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexDirection: { xs: "column", sm: "row" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Button
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleRequestRemoveCurrentDevice}
                disabled={loadingDevices}
                sx={{
                  borderColor: COLORS.MainColor,
                  color: COLORS.MainColor,
                  textTransform: "none",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Remove my device
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refreshNotificationDevices}
                sx={{
                  borderColor: COLORS.MainColor,
                  color: COLORS.MainColor,
                  textTransform: "none",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          <CustomTable<AdminNotificationDevice>
            filters={{ page: 0, size: 10 }}
            clientSidePagination
            refreshSignal={deviceRefreshToken}
            loading={loadingDevices}
            fields={[
              {
                key: "id",
                header: "Device",
                render: (row) => {
                  const meta = row.meta || {};
                  return renderExpandableText(
                    "Device",
                    meta.label || `${meta.os || "-"} ${meta.browser || ""}`,
                  );
                },
              },
              {
                key: "deviceType",
                header: "Type",
                transform: (row) => row.deviceType || "web",
              },
              {
                key: "meta",
                header: "Browser",
                render: (row) =>
                  renderExpandableText("Browser", row.meta?.browser || "-"),
              },
              {
                key: "meta",
                header: "OS",
                render: (row) => row.meta?.os || "-",
              },
              {
                key: "createdAt",
                header: "Created",
                transform: (row) =>
                  formatTableDateTime(row.createdAt || row.created_at),
              },
              {
                key: "endpoint",
                header: "Current",
                render: (row) =>
                  row.endpoint && row.endpoint === currentWebPushEndpoint ? (
                    <Chip
                      label="This browser"
                      size="small"
                      sx={{
                        bgcolor: "rgba(25,118,210,0.08)",
                        color: COLORS.MainColor,
                        fontWeight: 600,
                      }}
                    />
                  ) : (
                    "-"
                  ),
              },
            ]}
            fetchFunc={fetchNotificationDevices}
            actions={notificationDeviceActions}
          />
        </Box>
      </Box>

      <PinAccessDialog
        open={pinDialogOpen}
        onClose={() => {
          setPinDialogOpen(false);
          setPendingFinancePath(null);
        }}
        onVerified={() => {
          setFinanceUnlocked(true);
          setPinDialogOpen(false);
          if (pendingFinancePath) {
            navigate(pendingFinancePath);
            setPendingFinancePath(null);
          }
        }}
        title="Finance access"
        description="Enter the PIN to access admin finance pages."
      />

      <Dialog
        open={Boolean(expandedText)}
        onClose={() => setExpandedText(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{expandedText?.title || ""}</DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {expandedText?.value || ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setExpandedText(null)}
            sx={{ color: COLORS.MainColor, textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deviceToRemove)}
        onClose={() => setDeviceToRemove(null)}
        onConfirm={handleConfirmRemoveNotificationDevice}
        title="Remove notification device"
        message="Are you sure? This device will no longer receive web push notifications from this system."
        confirmButtonText="Remove"
      />
    </Box>
  );
};

export default AdminNotificationDevices;
